'use strict';
/**
 * Config-Namespace-Catalog — a curated registry of the cascading-configuration NAMESPACE KEYS the
 * platform authors, with human labels + a value-mode hint. This drives the viewer's left rail.
 *
 * The Configuration service has no "list all namespaces" endpoint, so this seed names the known keys;
 * the viewer ALSO discovers live NamespaceValues per key by calling /Configuration/Get without a
 * NamespaceValue (the response is keyed by NamespaceValue). The catalog is therefore a labelling +
 * grouping aid, not an exhaustive source of truth — unknown keys still work, they just render with a
 * derived label.
 *
 * NamespaceValueMode:
 *   'multi'  — many NamespaceValues under the key (e.g. MasterListCatalog has one per entity); enumerate live.
 *   'single' — effectively one well-known value; KnownValues names it.
 *
 * Keys verified in use across the Headlight app/services (MasterList-Registry-Provider,
 * ContractAdminConfig-Provider, and api/services config call sites).
 */

const CONFIG_NAMESPACE_CATALOG =
[
	{
		NamespaceKey: 'MasterListCatalog',
		Label: 'Master Lists',
		Group: 'Reference Data',
		NamespaceValueMode: 'multi',
		Description: 'Per-entity reference/catalog list configuration (one NamespaceValue per entity, e.g. Material, PayItem).',
	},
	{
		NamespaceKey: 'ModuleAccess',
		Label: 'Module Access',
		Group: 'Access & Permissions',
		NamespaceValueMode: 'multi',
		Description: 'Which optional product modules are enabled (one NamespaceValue per module, e.g. Contracts).',
	},
	{
		NamespaceKey: 'Permissions',
		Label: 'Permissions',
		Group: 'Access & Permissions',
		NamespaceValueMode: 'multi',
		Description: 'Role/permission toggles applied across the cascade (e.g. ContractAdmin).',
	},
	{
		NamespaceKey: 'PermissionsEnforcement',
		Label: 'Permissions Enforcement',
		Group: 'Access & Permissions',
		NamespaceValueMode: 'multi',
		Description: 'How permission rules are enforced at each scope.',
	},
	{
		NamespaceKey: 'EntityDestinations',
		Label: 'Where Records Open',
		Group: 'Platform',
		NamespaceValueMode: 'multi',
		Description: 'Destination rules per entity (NamespaceValue Sample, Location, Document): ordered rules keyed by name, each with an Audience, When conditions and a Target. Used by the links app for scanned labels and emailed links.',
	},
	{
		NamespaceKey: 'DashboardAccess',
		Label: 'Dashboard Access',
		Group: 'Platform',
		NamespaceValueMode: 'multi',
		Description: 'NamespaceValue Rules: ordered rules keyed by name, each giving an Audience of roles a set of Dashboards, an optional Landing dashboard, and optional DataFilters per dashboard. Resolved by platform data operations (PlatformDashboard/Available) for every app that shows dashboards.',
	},
	{
		NamespaceKey: 'QRCodes',
		Label: 'QR Codes',
		Group: 'Platform',
		NamespaceValueMode: 'multi',
		Description: 'Sample QR code profile (NamespaceValue Profile): code format, label envelope and SiteManager options, edited on the Sample QR Codes screen. Receiving (NamespaceValue Receive, edited on the Sample Scanning screen): optional StatusByLocation map of location QR code or GUID to the sample status a Receive scan sets, e.g. { "StatusByLocation": { "Disposed": "VOID" } }.',
	},
	{
		NamespaceKey: 'ApprovalsService',
		Label: 'Approvals',
		Group: 'Workflow',
		NamespaceValueMode: 'multi',
		Description: 'Approval-workflow configuration (routing, thresholds, notifications).',
	},
	{
		NamespaceKey: 'ApplicationSettings',
		Label: 'Application Settings',
		Group: 'Platform',
		NamespaceValueMode: 'multi',
		Description: 'General application behaviour and feature toggles.',
	},
	{
		NamespaceKey: 'FinanceService',
		Label: 'Finance',
		Group: 'Workflow',
		NamespaceValueMode: 'multi',
		Description: 'Finance/billing service configuration.',
	},
	{
		NamespaceKey: 'ObservationType',
		Label: 'Observation Types',
		Group: 'Field Data',
		NamespaceValueMode: 'multi',
		Description: 'Per-observation-type configuration overrides.',
	},
	{
		NamespaceKey: 'ObservationManifest',
		Label: 'Observation Manifests',
		Group: 'Field Data',
		NamespaceValueMode: 'multi',
		Description: 'Per-manifest configuration overrides.',
	},
	{
		NamespaceKey: 'Dashboards',
		Label: 'Dashboards',
		Group: 'Platform',
		NamespaceValueMode: 'multi',
		Description: 'Classic dashboards Fieldbook offers on a project (one NamespaceValue per dashboard), edited on the Fieldbook Dashboards screen.',
	},
	{
		NamespaceKey: 'EntityOwnershipAssignment',
		Label: 'Who Can Open New Forms',
		Group: 'Access & Permissions',
		NamespaceValueMode: 'multi',
		Description: 'NamespaceValue Document: { Enabled, ByType: { <form type>: { Roles: [ <role permission level> ] } } }. The API grants each new document to those roles. Edited on the Who Can Open New Forms screen.',
	},
	{
		NamespaceKey: 'FieldbookiOS',
		Label: 'Fieldbook iOS',
		Group: 'Platform',
		NamespaceValueMode: 'multi',
		Description: 'Read by the iPad app on sync. NamespaceValue Features: { QRScanner } (edited on the Sample Scanning screen); NamespaceValue SyncSettings: minimal-sync options.',
	},
];

/**
 * Find a catalog entry by namespace key (case-sensitive).
 * @param {string} pNamespaceKey
 * @return {object|null}
 */
function findNamespace(pNamespaceKey)
{
	return CONFIG_NAMESPACE_CATALOG.find((pEntry) => pEntry.NamespaceKey === pNamespaceKey) || null;
}

/**
 * Human label for a namespace key — the catalog label if known, else the raw key.
 * @param {string} pNamespaceKey
 * @return {string}
 */
function labelForNamespace(pNamespaceKey)
{
	const tmpEntry = findNamespace(pNamespaceKey);
	return (tmpEntry && tmpEntry.Label) || pNamespaceKey || '';
}

module.exports = {
	CONFIG_NAMESPACE_CATALOG,
	findNamespace,
	labelForNamespace,
};
